import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useDarkMode } from '../../context/DarkModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConflictReviewModal = ({ caseId, runId, matches, onClose, onSubmitted }) => {
  const { isDarkMode } = useDarkMode();
  const [decision, setDecision] = useState('');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const decisions = [
    {
      value: 'CLEAR',
      icon: CheckCircle,
      label: 'Godkjenn sak',
      description: 'Ingen konflikt - saken kan fortsette',
      color: 'emerald'
    },
    {
      value: 'CONFLICT',
      icon: XCircle,
      label: 'Marker konflikt',
      description: 'Det foreligger en interessekonflikt',
      color: 'red'
    },
    {
      value: 'ESCALATE',
      icon: AlertTriangle,
      label: 'Eskaler for review',
      description: 'Send til partner/admin for vurdering',
      color: 'purple'
    }
  ];

  const handleSubmit = async () => {
    if (!decision || !rationale.trim()) {
      toast.error('Vennligst velg beslutning og oppgi begrunnelse');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/conflicts/review/${caseId}`,
        {
          run_id: runId,
          decision,
          rationale: rationale.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Vurdering lagret');
      onSubmitted();
    } catch (error) {
      toast.error('Kunne ikke lagre vurdering');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`max-w-2xl w-full rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Vurder interessekonflikt
              </h2>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {matches.length} potensielle konflikt(er) funnet
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-400' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Decision Options */}
          <div>
            <label className={`block text-sm font-semibold mb-3 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Beslutning *
            </label>
            <div className="space-y-3">
              {decisions.map((option) => {
                const Icon = option.icon;
                const isSelected = decision === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setDecision(option.value)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? isDarkMode
                          ? 'border-purple-500 bg-purple-900/20'
                          : 'border-purple-500 bg-purple-50'
                        : isDarkMode
                        ? 'border-gray-700 hover:border-gray-600 bg-gray-900/50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${
                        isSelected
                          ? isDarkMode
                            ? 'text-purple-400'
                            : 'text-purple-600'
                          : isDarkMode
                          ? `text-${option.color}-400`
                          : `text-${option.color}-600`
                      }`} />
                      <div className="flex-1">
                        <div className={`font-semibold mb-1 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {option.label}
                        </div>
                        <div className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {option.description}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rationale */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Begrunnelse *
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Forklar hvorfor du tar denne beslutningen..."
              rows={4}
              className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                isDarkMode 
                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' 
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-purple-500`}
            />
            <p className={`text-xs mt-2 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              Din begrunnelse blir lagret i audit-loggen og kan sees av andre advokater.
            </p>
          </div>

          {/* Summary of Matches */}
          <div className={`p-4 rounded-xl border ${
            isDarkMode 
              ? 'bg-gray-900/50 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className={`text-sm font-medium mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Oppsummering av funn:
            </div>
            <ul className="space-y-1">
              {matches.slice(0, 3).map((match, idx) => (
                <li key={idx} className={`text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  • {match.matched_entity.name} ({match.confidence.toLowerCase()} tillit)
                </li>
              ))}
              {matches.length > 3 && (
                <li className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  ... og {matches.length - 3} til
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isDarkMode 
                  ? 'hover:bg-gray-700 text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              Avbryt
            </button>
            <button
              onClick={handleSubmit}
              disabled={!decision || !rationale.trim() || submitting}
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Lagrer...' : 'Lagre vurdering'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ConflictReviewModal;
